import React, { useRef, useEffect, useState } from 'react'
import moment from 'moment'
import { useTranslation } from 'react-i18next';
import { mmHgToKPa, celsiusToFahrenheit, percentTommHg,percentToKpa,mmHgToPercent, checkEmptyValue } from '../../plugins/common'
import { useRecoilState } from 'recoil';
import { selectBedInfoState,unitState } from '../../plugins/recoil';
import { ReactComponent as IconPrint } from '../../assets/icons/ic-print.svg'
import AlarmType from '../../assets/data/AlarmType.json'
import './TabularTrend.scss'

const alarmTypeMap = AlarmType[0]

// AlarmType field → tableColumn key 매핑
const ALARM_FIELD_TO_COLUMN_KEY = {
    'hr': 'pulseRate',
    'arrhythmia': 'pulseRate',
    'SpO2': 'spo2Percent',
    'spo2Percent': 'spo2Percent',
    'rrRpm': 'respRate',
    'nibp': 'nibp',
    'ibp1': 'ibp',
    'ibp2': 'ibp',
    'temp1C': 'temperatureCelsius',
    'temp2C': 'temperatureCelsius',
    'awrrRpm': 'respRate',
    'etco2': 'etCo2',
    'etCo2': 'etCo2',
}

// 알람 배경색 결정 함수
const getAlarmBgClass = (messageTimestamp, columnKey, alarms) => {
    if (!alarms?.length || !columnKey) return null

    const timestamp = moment(messageTimestamp)
    let highestPriority = null

    for (const alarm of alarms) {
        const startTime = moment(alarm.startTime)
        const endTime = moment(alarm.endTime)

        // 1. messageTimestamp가 알람 시간 범위(startTime ~ endTime)에 포함되는지 체크
        if (timestamp.isBefore(startTime) || timestamp.isAfter(endTime)) continue

        // 2. eventCode로 AlarmType에서 필드 조회
        const alarmFields = alarmTypeMap[String(alarm.eventCode)]
        if (!alarmFields) continue

        const fields = Array.isArray(alarmFields) ? alarmFields : [alarmFields]

        // 3. 해당 필드가 현재 컬럼과 매칭되는지 확인
        if (!fields.some(field => ALARM_FIELD_TO_COLUMN_KEY[field] === columnKey)) continue

        // 4. eventPriority 체크 (0, 1, 2만 배경색 적용)
        const priority = alarm.eventPriority
        if (priority !== 0 && priority !== 1 && priority !== 2) continue

        if (highestPriority === null || priority < highestPriority) {
            highestPriority = priority
        }
    }

    if (highestPriority === 0 || highestPriority === 1) return 'bg-[#605E2D]'
    if (highestPriority === 2) return 'bg-[#602D36]'
    return null
}

const TabularTrend = ({ tabularTrendData, handleTrendPrint, alarmList}) => {
    const { t } = useTranslation()
    const tableContainerRef = useRef(null)    
    const [selectBedInfo, ] = useRecoilState(selectBedInfoState)    
    const [units,] = useRecoilState(unitState);    

    // alarmList에서 events 배열 추출
    const alarmEvents = Array.isArray(alarmList) ? alarmList : (alarmList?.events || [])

    // otherTableHeaders 기반 테이블 컬럼 정의
    const tableColumns = [
        { label: t('leadModal.time'), key: 'messageTimestamp', unitKey: null, ratio: 1 },
        { label: t('leadModal.patientId'), key: 'devicePatientId', ratio: 1.5 },   
        { label: 'HR/PR', key: 'pulseRate', unitKey: 'HR', ratio: 1 },
        { label: 'SpO2', key: 'spo2Percent', unitKey: 'SpO2', ratio: 1 },
        { label: 'Temp', key: 'temperatureCelsius', unitKey: 'Temp1', ratio: 1 },
        { label: 'RESP', key: 'respRate', unitKey: 'RR', ratio: 1 },
        { label: 'NIBP', key: 'nibp', unitKey: 'NIBP', ratio: 1 },
        { label: 'IBP', key: 'ibp', unitKey: 'IBP1', ratio: 1 },
        { label: 'EtCO2', key: 'etCo2', unitKey: 'EtCO2', ratio: 1 },
        { label: 'InCO2', key: 'fiCo2', unitKey: 'InCO2', ratio: 1 },
        { label: 'IPI', key: 'ipi', unitKey: 'IPI', ratio: 1 },
    ]

    const totalRatio = tableColumns.reduce((sum, col) => sum + col.ratio, 0) // 11.5

    const convertUnit = (parameter,value) => {
        const nibpSeries = ['NIBP']
        const ibpSeries = ['IBP1', 'IBP2', 'IBP3', 'IBP4']
        const co2Series = ['EtCO2', 'InCO2']
        const tempSeries = ['Temp1', 'Temp2']        
        const gasSeries = ['gasCo2', 'gasDes', 'gasN2o', 'gasO2', 'gasSev']
        const otherSeries = ['HR', 'RR', 'awRR', 'SpO2', 'arrhythmia', 'IPI']        

            //함수를 유동적으로 사용해보고 싶어서 써봄.
        const convertFunctions = {
            mmHgToKPa,
            celsiusToFahrenheit,
            percentTommHg,
            percentToKpa,
            mmHgToPercent
        };

        let unit = undefined;
        let functionName = undefined;                

        if (nibpSeries.includes(parameter)) {
            unit = units["NIBP"];
            if(unit === 'kPa') {
                functionName = 'mmHgToKPa'; // mmHg를 kPa로 변환하는 함수
            }
        } else if (ibpSeries.includes(parameter)) {
            const prefix = parameter.match(/^IBP[1-4]/)?.[0];  // "IBP1", "IBP2" 등 추출
            unit = units[prefix];
            if(unit === 'kPa') {
                functionName = 'mmHgToKPa'; // mmHg를 kPa로 변환하는 함수
            }
        } else if (co2Series.includes(parameter)) {            
            unit = units[parameter];            
            if(unit === 'kPa') {
                functionName = 'mmHgToKPa'; // mmHg를 kPa로 변환하는 함수
            }
            else if(unit === '%'){
                functionName = 'mmHgToPercent'; // mmHg를 %로 변환하는 함수
            }
        } else if (tempSeries.includes(parameter)) {
            unit = units[parameter];
            if(unit === '°F') {
                functionName = 'celsiusToFahrenheit'; // 섭씨를 화씨로 변환하는 함수
            }
        } 
        else if (gasSeries.includes(parameter)) {                       
            unit = units['GAS'];
            if(unit === 'mmHg') {
                functionName = 'percentTommHg'; // mmHg를 kPa로 변환하는 함수
            }
            else if(unit === 'kPa'){
                functionName = 'percentToKpa';
            } 
        } else if(otherSeries.includes(parameter)) {
            unit = units[parameter];
        }

        let label = value;
        if (functionName && typeof convertFunctions[functionName] === 'function') {
            if(nibpSeries.includes(parameter) || ibpSeries.includes(parameter) || gasSeries.includes(parameter)){                
                const splitValue = value.split('/');
                let newLabel = '';
                splitValue.map((value,index) => {                    
                    const convertedValue = value === '-' ? '-' : String(convertFunctions[functionName](value));
                    newLabel += convertedValue + `${index == splitValue.length - 1 ? '' : '/'}`;                    
                })
                label = newLabel;
            }
            else {
                if(value == '-'){
                    return value;
                }else{
                    label = convertFunctions[functionName](value);
                }
            }
        }
        return label;
    }

    // 셀 값 포맷팅 함수
    const getFormatValue = (col, item) => {
        const rawValue = item[col.key]
        
        // Unit 변환이 필요 없는 컬럼
        if (!col.unitKey) return checkEmptyValue(rawValue)
        
        // Temperature - 소수점 한자리 포맷
        if (col.key === 'temperatureCelsius') {
            if (rawValue == null) return '-'
            const formatted = (Math.floor(rawValue * 10) / 10).toFixed(1)
            return convertUnit(col.unitKey, formatted)
        }
        
        // Unit 변환
        const value = checkEmptyValue(rawValue)
        return convertUnit(col.unitKey, String(value))
    }

    useEffect(() => {
        if (tableContainerRef.current) {
            tableContainerRef.current.scrollLeft = 0            
        }
    }, [tabularTrendData])

    return (
        <div className="w-full h-full relative bg-[#2F2F2F]">
            <div className="relative flex justify-between items-center h-[40px] px-[20px]">
                <h2 className="text-white text-center font-medium text-lg leading-[110%] font-pretendard">{t('trend.tabularTrend')}</h2>                
                <div className='flex flex-nowrap gap-[12px]'>
                    <div className="flex items-center justify-center cursor-pointer text-white rounded-[4px] border border-[#5E5E5E] bg-gradient-to-b from-[#5E5E5E] to-[#383838] p-[4px]" onClick={handleTrendPrint}>
                        <IconPrint className="w-[18px] h-[18px] text-white" />
                    </div>                    
                </div>                
            </div>   
            <div className={`flex-1 trend-review-wrapper h-full`} style={{ minHeight: `calc(100% - 40px)`, maxHeight: 'calc(100% - 40px)'}}>
                <div ref={ tableContainerRef } className=" table-container scrollbar scrollbar-x scrollbar-x-10px">
                    <table style={{ borderCollapse: "collapse", width: '100%', textAlign: "center", tableLayout: 'fixed', ...(tabularTrendData?.length > 0 ? {} : { height: '100%' }) }}>
                        <thead>
                            <tr style={{ borderBottomWidth: 1, borderColor: '#4E4E4E' }}>
                                {
                                    tabularTrendData?.length > 0 && (
                                        tableColumns.map((col, idx) => {
                                            const isLast = idx === tableColumns.length - 1
                                            return (
                                                <th
                                                    key={`trend-review-header-${idx}`}
                                                    className={`${!isLast ? 'right-border' : ''} bg-[#262626]`}
                                                    style={{
                                                        padding: '0 12px', height: 34, fontSize: 14, lineHeight: '15.4px',
                                                        fontWeight: 300, color: '#dedede', position: 'sticky', top: -1, zIndex: 2,
                                                        width: `${(col.ratio / totalRatio * 100).toFixed(2)}%`
                                                    }}
                                                >
                                                    {col.label}
                                                    {col.unitKey && units[col.unitKey] && ` (${units[col.unitKey]})`}
                                                </th>
                                            )
                                        })
                                    )
                                }
                            </tr>
                        </thead>
                        <tbody>
                        {
                            tabularTrendData?.length > 0 ?                        
                            tabularTrendData?.map((item, idx) => {                                
                                return (
                                    <tr key={`trend-review-data-key-${idx}`} className={`${idx % 2 === 1 ? 'highlight' : ''}`}>
                                        <td
                                            className={`${idx % 2 === 1 ? 'bg-[#383838]' : 'bg-[#2F2F2F]'}`}
                                            style={{ padding: '0 12px', height: 34, fontSize: 14, lineHeight: '15.4px', fontWeight: 300, color: '#dedede' }}                                            
                                        >
                                            {moment(item['messageTimestamp']).format('YYYY-MM-DD HH:mm:ss')}
                                        </td>
    
                                        {
                                            tableColumns.slice(1).map((col, colIdx) => {
                                                // 값이 비어있으면 알람 배경색 미적용
                                                const rawValue = item[col.key]
                                                const hasValue = rawValue != null && rawValue !== '' && !/^[-/]+$/.test(String(rawValue))
                                                const alarmBg = col.unitKey && hasValue ? getAlarmBgClass(item.messageTimestamp, col.key, alarmEvents) : null
                                                const normalBg = idx % 2 === 1 ? '' : 'bg-[#2F2F2F]'
                                                const bgClass = alarmBg || normalBg
                                                return (
                                                    <td 
                                                        key={`trend-review-data-${idx}-${col.key}-${colIdx}`}                                                                                                        
                                                        className={`${colIdx !== tableColumns.length - 2 ? 'right-border' : ''} ${bgClass} truncate max-w-0`}                                                       
                                                        style={{ padding: '0 12px', height: 34, fontSize: 14, lineHeight: '15.4px', fontWeight: 300, color: '#dedede' }}
                                                    >
                                                            {getFormatValue(col, item)}
                                                    </td>
                                                )
                                            }) 
                                        }
                                    </tr>
                                )
                            }) :
                            <tr>
                                <td style={{ height: '100%', textAlign: 'center', verticalAlign: 'middle', color: '#dedede', fontSize: 14 }}>
                                    {t('error.noDataAvailable')}
                                </td>
                            </tr>
                        }                    
                        </tbody>
                    </table>
    
                </div>
            </div>
        </div>
    );
};

export default TabularTrend;
